import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useReduxSelector } from "@hooks/useReduxSelector";
import { advantageTypeActions } from "@store/actions";

import "@css/pages/employer/DocumentsEmployers/courseCertifications.css";
import "@css/pages/employer/listeEmployer.css";
import IconesRecutement from "@image/icones/DocumentsEmployers/briefcase.svg";

import AddButton from "@components/buttons/AddButton";

import { CONSTANT_SOCKET_FOR_EMPLOYEE_AND_SALARY } from "@helpers/_constants";
import { EmployerSalary } from "@services/all.services";

import CareerList from "./CareerList";
import EndJob from "./EndJob";
import SalaryEvolutionGraph from "./SalaryEvolutionGraph";
import { ModalCourseForm } from "./modals";
import { SORT_FUNCTION } from "./utils";

export default function Career(props) {
  const { careerList } = props;

  const socket = useReduxSelector(({ abstract }) => abstract.socket);
  const idEnterprise = useReduxSelector(
    ({ userConnected }) => userConnected.Enterprise
  );
  const { t: translator } = useTranslation(
    "pages.employés.parcours&Certifications"
  );
  const reduxDispatch = useDispatch();
  const advantageType = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.advantageType
  );
  const [selectedSortType, setSelectedSortType] = useState("date_desc");
  const sortedCareerList = useMemo(
    () => [...careerList].sort(SORT_FUNCTION[selectedSortType]),
    [selectedSortType, careerList]
  );

  const [showCourseModal, setShowCourseModal] = useState(false);

  function toggleCourseModal() {
    setShowCourseModal((_state) => !_state);
  }

  const [employeeInfo, setEmployeeInfo] = useState(null);

  const loadEmployeeInfoAsync = useCallback(async () => {
    try {
      if (!props.employeeId) {
        return;
      }
      const response = await EmployerSalary.getByEmployer(props.employeeId);
      if (!response?.Employer) {
        return;
      }

      const employeeData = response?.Employer;

      setEmployeeInfo({
        status: employeeData.status,
        dateEndEmploi: employeeData.dateEndEmploi,
        endJobDescription: employeeData.descriptionEndEmploi,
        hiringDate: response.job[0].hiringDate
      });
    } catch (err) {
      console.error(err);
    }
  }, [props.employeeId]);

  useEffect(() => {
    if (!props.isSaving && !(props.hiringDate instanceof moment)) {
      setEmployeeInfo({
        ...employeeInfo,
        hiringDate: props.hiringDate
      });
    }
  }, [props.isSaving]);

  useEffect(() => {
    if (!socket) return;

    const handleEmployeeAndSalary = (employee) => {
      const employees = Array.isArray(employee) ? employee : [employee];
      if (employees.some((item) =>
        item?.Enterprise?.toString() === idEnterprise?.toString() &&
        (!item?._id || item._id.toString() === props.employeeId?.toString())
      )) {
        return loadEmployeeInfoAsync();
      }
    };

    socket.on(CONSTANT_SOCKET_FOR_EMPLOYEE_AND_SALARY, handleEmployeeAndSalary);
    return () => socket.off(CONSTANT_SOCKET_FOR_EMPLOYEE_AND_SALARY, handleEmployeeAndSalary);
  }, [socket, idEnterprise, props.employeeId, loadEmployeeInfoAsync]);

  useEffect(() => {
    loadEmployeeInfoAsync();
  }, [loadEmployeeInfoAsync]);

  useEffect(() => {
    if (!advantageType.isLoaded) {
      reduxDispatch(advantageTypeActions.loadData);
    } else if (advantageType.isDataOutdated) {
      reduxDispatch(advantageTypeActions.refreshList);
    }
  }, [
    advantageType.isLoaded,
    advantageType.isDataOutdated,
    advantageType.invalidationVersion,
    reduxDispatch
  ]);

  return (
    <>
      <div className="arh-w-full arh-pb-8 arh-pl-24 arh-pr-4">
        {!props.readOnly && (
          <div
            className="arh-my-6 arh-flex arh-items-center arh-space-x-6"
            id="ModalsParcours"
          >
            <AddButton
              label={translator("Ajouter un poste ou un événement ponctuel")}
              onClick={toggleCourseModal}
              color="arh-bg-greenColor hover:arh-bg-darkGreenColor"
              btnSize="arh-w-[30px] arh-h-[30px]"
            />
          </div>
        )}

        <div className="content_course">
          <div className="items_content_course">
            <div className="align-items-center arh-mb-4 arh-flex arh-h-12 arh-w-full arh-bg-cardGreen arh-px-12 arh-text-[13px] arh-font-medium arh-text-greenColor">
              <div className="arh-w-0 arh-text-[13px] arh-font-medium arh-text-darkColor">
                <img
                  alt="recurtement"
                  className="icones_recrutement GreenThemeSelect"
                  src={IconesRecutement}
                />
              </div>
              <div className="arh-mx-8 arh-font-poppins arh-text-[13px] arh-font-medium arh-text-darkColor">
                {translator("Entrée en fonction")} :{" "}
                {employeeInfo?.hiringDate
                  ? moment(employeeInfo.hiringDate).format("LL")
                  : ""}
              </div>
            </div>
            <table className="arh-w-full arh-table-fixed arh-font-poppins">
              <colgroup>
                <col className="arh-w-[28%]" />
                <col className="arh-w-[27%]" />
                <col className="arh-w-[13%]" />
                <col className="arh-w-[27%]" />
                <col className="arh-w-[5%]" />
              </colgroup>
              <thead>
                <tr className="arh-h-12 arh-bg-greyColor">
                  <th className="arh-pl-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                    {translator("Nom du poste")}
                  </th>
                  <th className="arh-text-xs arh-font-semibold arh-text-darkColor">
                    <div className="blackSvgColor arh-mr-auto arh-flex arh-items-center arh-justify-start">
                      <span>{translator("Dates")}</span>
                      <span
                        className="arh-ml-2 arh-cursor-pointer"
                        onClick={() => {
                          if (selectedSortType === "date_asc") {
                            setSelectedSortType("date_desc");
                          } else {
                            setSelectedSortType("date_asc");
                          }
                        }}
                      ></span>
                    </div>
                  </th>
                  <th className="arh-text-xs arh-font-semibold arh-text-darkColor">
                    <div className="blackSvgColor arh-mr-auto arh-flex arh-items-center arh-justify-start">
                      <span>{translator("Notes")}</span>
                    </div>
                  </th>
                  <th className="arh-whitespace-nowrap arh-text-xs arh-font-semibold arh-text-darkColor">
                    {translator("Description du poste")}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {/** LISTES DES PARCOURS */}
                <CareerList
                  careerList={sortedCareerList}
                  deviceList={props.deviceList}
                  readOnly={props.readOnly}
                  hiringDate={employeeInfo?.hiringDate}
                  employeeId={props.employeeId}
                  employeePoste={props.employeePoste}
                />
              </tbody>
            </table>
            {props?.readOnly && !props?.isEnterprisePage ? (
              ""
            ) : (
              <SalaryEvolutionGraph
                loadOnMount={props.loadOnMount}
                deviseList={props.deviceList}
                employeeId={props.employeeId}
              />
            )}

            {/* COMPOSANT POUR LA DATE DE FIN D'EMPLOI */}
            <EndJob
              employeeId={props.employeeId}
              dateEndEmploi={employeeInfo?.dateEndEmploi}
              endJobDescription={employeeInfo?.endJobDescription}
              status={employeeInfo?.status}
              dispositiveClass="display_Desktop"
            />
          </div>
        </div>
      </div>

      <ModalCourseForm
        isOpen={showCourseModal}
        onClickClose={toggleCourseModal}
        employeeId={props.employeeId}
        hiringDate={employeeInfo?.hiringDate}
      />
    </>
  );
}
